import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  StyledModalContent,
  TitleText,
} from "./QueryScopeModal";
import StyledText from "../../components/StyledComponents/StyledText";
import { CorporateTheme } from "../../utils/themes";
import { Header } from "../forms/CustomForms/ShareFormModal";
import styled from "styled-components";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import DocumentIcon from "../../assets/icons/document-text";
import CustomSelect from "../../components/form/CustomSelect";
import {
  useBulkRolesMutation,
  useGetContactRolesQuery,
} from "../../api/searchQueryApiSlice";
import { useToast } from "../../components/toast/ToastContext";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { SkeletonCell } from "../../components/SkeletonCell";
import MultiSelectDropdown from "../../components/form/MultiSelectDropdown";
import { useTheme } from "../../middleware/ThemeContext";
import AMSSelect from "../../components/AMSSelect";
import SaveIcon from "../../assets/icons/SaveIcon";

type AssignContactRoleModalProps = {
  title?: string;
  subTitle?: string;
  onClose: () => void;
  ids?: string[]; // or number[] or appropriate type for your IDs
  refetch?: () => void;
  setResultSelectedIds?: (ids: string[]) => void;
};

const AssignContactRoleModal: React.FC<AssignContactRoleModalProps> = ({
  title,
  subTitle,
  onClose,
  refetch,
  ids,
  setResultSelectedIds,
}) => {
  const { theme } = useTheme();
  const [contactRole, setContactRole] = useState<number[]>([]);
  const [contactRoleError, setContactRoleError] = useState("");
  const [addBulkRoles, { isLoading: bulkRolesLoading }] =
    useBulkRolesMutation();
  const showToast = useToast();
  const { data: roles, isLoading } = useGetContactRolesQuery({});

  const contactRoleOptions = roles?.roles ?? [];
  const contactRoleSelectOptions = useMemo(
    () =>
      (contactRoleOptions as any[])?.map((role: any) => ({
        id: role?.id,
        label: role?.name,
        value: String(role?.id),
      })) ?? [],
    [contactRoleOptions]
  );
  const handleSubmit = async () => {
    if (!contactRole?.length) {
      setContactRoleError("Contact Role field is required.");
      return;
    }

    setContactRoleError(""); 

    const val = {
      contactIds: ids?.map(Number) ?? [],      
      roleIds: contactRole.map(Number),       
    };

    try {
      
      const res = await addBulkRoles(val).unwrap();

      
      showToast(
        "Contact Role assigned",
        "Contact roles have been assigned.",
        "success"
      );

      if (refetch) refetch();
      if (setResultSelectedIds) setResultSelectedIds([]);
      onClose();
    } catch (error: any) {
      
      console.error("Failed to assign contact roles:", error);

      
      const message =
        error?.data?.message ||
        error?.data?.detail ||
        error?.data ||
        error?.message ||
        "Failed to assign contact role";

      showToast("Action Failed", String(message), "error");
    }
  };


  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <StyledModalContent theme={theme}>
        <ModalHeader>
          <Header>
            <StyledCloseButton onClick={onClose} style={{ color: theme.CorporateTheme.colors.textSecondary }}>×</StyledCloseButton>
            <ModalTitle className="pb-3 pt-1 mt-4">
              <TitleText theme={theme}>{title ?? "Assign Contact Role"}</TitleText>
              <StyledText
                fontSize="14px"
                fontWeight={500}
                textColor={theme.CorporateTheme?.colors?.textSecondary}
              >
                {subTitle ??
                  "Allows users to assign or update contact roles for multiple records simultaneously using the bulk action feature."}
              </StyledText>
            </ModalTitle>
          </Header>
        </ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-center  px-1 py-3 border rounded mt-4 mx-5">
            {isLoading ? (
              <div className="d-flex flex-column gap-2 w-100 ps-4">                
              <SkeletonCell
                style={{
                  maxWidth: "100px",
                  height: "36px",
                  marginBottom: "10px",
                }}
              />
                <SkeletonCell
                  style={{
                    maxWidth: "400px",
                    height: "36px",
                    marginBottom: "10px",
                  }}
                />
                <SkeletonCell
                  style={{
                    maxWidth: "100px",
                    height: "36px",
                    marginBottom: "10px",
                  }}
                />
                <SkeletonCell
                  style={{
                    maxWidth: "400px",
                    height: "36px",
                    marginBottom: "10px",
                  }}
                />
              </div>
            ) : (
              <div className="p-4 w-100">
                <div className="mb-4">
                  <StyledText
                    className=" mb-2"
                    textColor={theme.CorporateTheme?.colors?.headerColor}
                  >
                    Selected Records
                  </StyledText>
                  <StyledText
                    className=" mb-2"
                    fontWeight={500}
                    textColor={theme.CorporateTheme?.colors?.textSecondary}
                  >
                    {ids?.length}
                  </StyledText>
                </div>

                <div className="mt-4 d-flex flex-column gap-3">
                  <StyledText textColor={theme.CorporateTheme?.colors?.headerColor}>
                    Assign Contact Role <span className="text-danger ">*</span>
                  </StyledText>

                  <div className="d-flex flex-column">
                    <AMSSelect
                      placeholder="select contact role here"
                      staticOptions={contactRoleSelectOptions}
                      cacheUniqs={[contactRoleSelectOptions.length]}
                      isMulti
                      value={
                        (contactRoleSelectOptions ?? []).filter((opt: any) =>
                          contactRole.some((id) => String(id) === String(opt.value))
                        )
                      }
                      onChange={(opts: any) => {
                        const selected = Array.isArray(opts) ? opts : [];
                        const updatedRoles = selected
                          .map((o: any) => Number(o?.value))
                          .filter((n: number) => !Number.isNaN(n));
                        setContactRole(updatedRoles);
                        if (updatedRoles.length > 0) setContactRoleError("");
                      }}
                      menuPosition="fixed"
                    />
                    {contactRoleError && (
                      <span
                        className="text-danger mt-1"
                        style={{ fontSize: "13px" }}
                      >
                        {contactRoleError}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-end mt-3 me-5">
            <div className="d-flex gap-1">
              <Button
                theme={theme}
                type="button"
                className={`cursor-pointer ${bulkRolesLoading ? "class-disable" : ""
                  }`}
                $variant="cancel"
                onClick={onClose}
              >
                <CrossIcon />
                Cancel
              </Button>
              <Button
                theme={theme}
                type="submit"
                $variant="add"
                className={`cursor-ponter ${bulkRolesLoading ? "class-disable" : ""
                  } `}
                onClick={handleSubmit}
              >
                {bulkRolesLoading ? (
                  <>
                    <label>Saving...</label>
                    <LoadingCircleIcon color={theme.CorporateTheme.colors.background} />
                  </>
                ) : (
                  <div
                    className={`d-flex align-items-center gap-1 cursor-pointer`}
                  >
                    <label className="cursor-pointer">
                      <SaveIcon color={theme.CorporateTheme.colors.background} /> Save{" "}
                    </label>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter />
      </StyledModalContent>
    </Modal>
  );
};

export default AssignContactRoleModal;

const StyledCloseButton = styled(ModalCloseButton)`
  top: 10px;
`;
const StyledIntervalButton = styled.div`
  cursor: pointer;
  &.active {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #003366;
    color: #fff;
    height: 33px;
    width: 100%;
    border-radius: 5px;
    padding: 0 10px;
  }
`;
